import { OrgMemberDetail } from "@garther/interfaces";
import { Link, useLocation, useRouterState } from "@tanstack/react-router";
import { ChevronRight, Menu } from "lucide-react";
import { Fragment } from "react";

import { AuthButton } from "@/features/auth/components/auth-button";

import { OrgSwitcher } from "./org-switcher";

const SECTION_LABELS: Record<string, string> = {
  "": "Tổng quan",
  events: "Sự kiện",
  event: "Sự kiện",
  locations: "Địa điểm",
  members: "Thành viên",
  referrals: "Giới thiệu",
  edit: "Chỉnh sửa"
};

type BreadcrumbItem = {
  label: string;
  to?: string;
  params?: Record<string, string>;
};

interface TopbarProps {
  orgId?: string;
  orgs?: OrgMemberDetail[];
  onMenuClick: () => void;
}

export function TopBar({ orgId, orgs, onMenuClick }: TopbarProps) {
  const { pathname } = useLocation();
  const routerState = useRouterState();

  // Attempt to read org name from route context
  const orgContext = routerState.matches.find(
    (m): m is typeof m & { context: { org: { id: string; name: string } } } =>
      !!m.context && typeof m.context === "object" && "org" in m.context
  )?.context;
  const org = orgContext?.org;

  // Parse path segments after /org/$orgId
  const orgMatch = pathname.match(/\/org\/([^/]+)(\/(.+))?/);
  const subPath = orgMatch?.[3] ?? "";
  const topSection = subPath.split("/")[0] ?? "";

  const breadcrumb: BreadcrumbItem[] = [];

  if (org) {
    breadcrumb.push({
      label: org.name,
      to: "/org/$orgId",
      params: { orgId: org.id }
    });
  }

  const sectionLabel = SECTION_LABELS[topSection];
  if (sectionLabel) {
    breadcrumb.push({ label: sectionLabel });
  } else if (!org) {
    breadcrumb.push({ label: "Dashboard" });
  }

  return (
    <header className="flex items-center justify-between flex-shrink-0 mt-1 mb-2">
      <nav className="flex items-center gap-1.5">
        <button onClick={onMenuClick} className="md:hidden mr-1 text-slate-500 hover:text-primary-700">
          <Menu size={20} />
        </button>
        {breadcrumb.map((item, i) => (
          <Fragment key={i}>
            {i > 0 && <ChevronRight size={13} className="text-slate-400" />}
            {item.to ? (
              item.to === "/org/$orgId" && orgId && orgs ? (
                <OrgSwitcher className="" currentOrgId={orgId} orgs={orgs} />
              ) : (
                <Link
                  to={item.to}
                  params={item.params}
                  className="text-responsive-md text-slate-500 hover:text-primary-700 transition-colors"
                >
                  {item.label}
                </Link>
              )
            ) : (
              <span className="text-responsive-lg font-normal text-text-primary uppercase font-literata">
                {item.label}
              </span>
            )}
          </Fragment>
        ))}
      </nav>
      <AuthButton />
    </header>
  );
}
